import { useState, useContext } from 'react';
import * as Sentry from '@sentry/react';
import isDate from 'date-fns/isValid';
import dateFormat from 'date-fns/format';
import { makeStyles } from '@material-ui/core/styles';
import MaskedInput from 'react-text-mask';
import emailMask from 'text-mask-addons/dist/emailMask';
import Grid from '@material-ui/core/Grid';
import CheckIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpIcon from '@material-ui/icons/HelpOutline';
import Progress from '@material-ui/core/CircularProgress';
import Typography from 'components/CncUI/Typography';
import TextField from 'components/CncUI/TextField';
import TextFieldSsn from 'components/CncUI/TextField/TextFieldSsn';
import TextFieldSin from 'components/CncUI/TextField/TextFieldSin';
import DatePicker from 'components/CncUI/DatePicker';
import Radio from 'components/CncUI/Radio';
import Button from 'components/CncUI/Button';
import Tooltip from 'components/CncUI/Tooltip';

import withPermissionProtection from 'hoc/withPermissionProtection';
import SnackbarContext from 'context/Snackbar/Context';
import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';

import emailBlacklist from 'constants/registrationEmailDomainBlacklist';
import isEmail from 'utilities/validateEmail';
import format from 'utilities/format';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints, spacing }) => ({
  header: {
    marginTop: 40,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  addressesStatus: {
    display: 'flex',
    alignItems: 'center',
    '& .cncui-typography': {
      marginLeft: 8,
    },
  },
  tooltipIcon: {
    marginLeft: spacing(1),
    height: 15,
    width: 15,
    color: palette.cncUI.misc.link,
    verticalAlign: 'text-bottom',
  },
  grey: {
    color: palette.cncUI.primary.gray['+3'],
  },
  progress: {
    color: palette.cncUI.default.primary,
  },
  success: {
    color: palette.cncUI.alert.green.primary,
  },
  danger: {
    color: palette.cncUI.alert.red.primary,
  },
  control: {
    marginTop: 24,
    marginBottom: 16,
    textAlign: 'center',
    '& .cncui-button': {
      width: 150,
    },
    [breakpoints.up('sm')]: {
      textAlign: 'right',
    },
  },
}));

const UserRegistration = () => {
  const logger = Logger('admin.userRegistration');
  const classes = useStyles();
  const notify = useContext(SnackbarContext);
  const getVerificationAddresses = useQuery('userVerificationAddresses', {
    fetchPolicy: 'network-only',
    request: input => ({ input }),
    response: res => res.data.userVerificationAddresses,
  });
  const verifyEmployee = useMutation('AdminVerifyEmployee', {
    scopes: 'employee_registrations:create',
    request: input => input,
    response: res => res.data.verifyEmployee,
  });
  const [form, setForm] = useState({
    countryFlag: 'US',
    idType: 'ssn',
    firstName: '',
    lastName: '',
    birth: null,
    ssn: '',
    email: '',
    confirm: '',
    address: '',
  });
  const [state, setState] = useState({
    error: '',
    errorSave: '',
    loading: false,
    loadingSave: false,
    addresses: [],
  });
  const update = data => setForm(old => ({ ...old, ...data }));
  const modify = data => setState(old => ({ ...old, ...data }));

  const updateIdNumber = e => {
    const value = e.target.value;
    const { idType, countryFlag } = form;
    update({ ssn: value });
    const isValid = format[idType] || format.ssn;
    if (isValid(value)) {
      const idn = value;
      modify({ loading: true, error: '' });
      getVerificationAddresses({ countryFlag, idType, idn })
        .then(addresses => modify({ addresses }))
        .catch(err => {
          modify({ error: err.message });
          logger.error(`ERROR: ${err.message}`);
        })
        .finally(() => modify({ loading: false }));
    } else {
      modify({ addresses: [], error: '' });
      update({ address: '' });
    }
  };

  const updateIdType = e => {
    const currentSsn = form.ssn;
    update({ idType: e.target.value, ssn: '' });
    update({ ssn: currentSsn });
  };

  const updateCountry = e => {
    const currentSsn = form.ssn;
    update({
      countryFlag: e.target.value,
      idType: e.target.value === 'US' ? 'ssn' : 'sin',
      ssn: '',
    });
    update({ ssn: currentSsn });
  };

  const validate = () => {
    const errors = [];
    const missing = [];
    const required = {
      firstName: 'First Name',
      lastName: 'Last Name',
      birth: 'Date of Birth',
      email: 'Email',
    };
    const ssn = (form.ssn || '').replace(/\D/g, '');
    const isValid = format[form.idType] || format.ssn;
    const ssnTitle =
      form.idType === 'ssn'
        ? 'Social Security Number'
        : 'Social Insurance Number';
    const blackListed = email =>
      emailBlacklist.some(blackListedEmail => email.endsWith(blackListedEmail));
    Object.keys(required).forEach(key => {
      if (!form[key]) {
        missing.push(required[key]);
      }
    });
    if (!ssn) missing.push(ssnTitle);
    if (missing.length) {
      errors.push(`Missing ${missing.join(', ')}`);
    }
    if (ssn && !isValid(ssn)) errors.push(`${ssnTitle} value is not valid.`);
    if (form.email && !isEmail(form.email)) {
      errors.push('Email value is not valid.');
    } else if (form.email && isEmail(form.email) && blackListed(form.email)) {
      errors.push('Email value is black listed.');
    } else if (form.email && form.email !== form.confirm) {
      errors.push('Email confirmation must match email value.');
    }
    if (form.birth && !isDate(form.birth)) {
      errors.push('Birth date value is not valid');
    }
    if (!form.address) {
      errors.push('An address must be selected.');
    }

    if (errors.length) {
      modify({
        errorSave: (
          <div>
            <strong>Some errors ocurred:</strong>
            {errors.map(err => (
              <ul key={err}>
                <li>{err}</li>
              </ul>
            ))}
          </div>
        ),
      });
      return false;
    }

    modify({ errorSave: '' });
    return true;
  };

  const reset = () => {
    modify({ error: '', errorSave: '', addresses: [] });
    update({
      countryFlag: 'US',
      idType: 'ssn',
      firstName: '',
      lastName: '',
      birth: null,
      ssn: '',
      email: '',
      confirm: '',
      address: '',
    });
  };

  const submit = () => {
    if (!validate()) return;
    modify({ loadingSave: true });
    notify({
      type: 'info',
      show: true,
      title: '',
      message: `Attempting to Register the user...`,
      duration: 4500,
    });
    verifyEmployee({
      countryFlag: form.countryFlag,
      idType: form.idType,
      ssn: form.ssn,
      firstName: form.firstName,
      lastName: form.lastName,
      dob: dateFormat(form.birth, 'MM/dd/yyyy'),
      email: form.email,
      address: form.address,
    })
      .then(() => {
        reset();
        notify({
          type: 'success',
          show: true,
          title: 'Success',
          message: `Registration submitted successfully.
            Please check registration log for status.`,
          duration: 4500,
        });
      })
      .catch(err => {
        const { message } = err;
        const error = message.startsWith('GraphQL error:')
          ? message.substring(15)
          : message;
        Sentry.withScope(scope => {
          scope.setExtra(
            'message',
            'Could not submit user verification from Admin Form',
          );
          scope.setExtra('formData', form);
          logger.error(err);
        });
        logger.error(`ERROR: ${error}`, err);
        notify({
          type: 'error',
          show: true,
          title: 'Error ocurred',
          message: error,
          duration: 4500,
        });
      })
      .finally(() => modify({ loadingSave: false }));
  };

  const addressIcon = () => {
    if (state.loading)
      return <Progress className={classes.progress} size={15} />;
    if (state.error) return <CancelIcon className={classes.danger} />;
    if (state.addresses.length > 0)
      return <CheckIcon className={classes.success} />;
    return <CheckIcon className={classes.grey} />;
  };

  return (
    <Grid container spacing={2} direction="column">
      <Grid item xs={12} sm={9} md={6} id="HeaderTitle">
        <Typography variant="h2" className={classes.header}>
          User Registration
        </Typography>
      </Grid>
      <Grid item xs={12} sm={9} md={6}>
        <Typography>
          In which database does this user currently reside?
          <Tooltip
            enterTouchDelay={100}
            position="right-center"
            content={`This determines which databases are used to search for the employee.
                  Their info must exist within our database in order for them to be registered.`}
          >
            <HelpIcon className={classes.tooltipIcon} />
          </Tooltip>
        </Typography>
        <Radio.Group
          id="CountryFlag"
          value={form.countryFlag}
          onChange={updateCountry}
        >
          <Grid container spacing={2}>
            <Grid item>
              <Radio value="US" id="CountryFlag-US">
                United States
              </Radio>
            </Grid>
            <Grid item>
              <Radio value="CAN" id="CountryFlag-CAN">
                Canada
              </Radio>
            </Grid>
          </Grid>
        </Radio.Group>
      </Grid>

      <Grid item xs={12} sm={9} md={6}>
        <TextField
          light
          fullWidth
          id="firstName"
          label="First Name"
          placeholder="First Name"
          value={form.firstName}
          onChange={e => update({ firstName: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={9} md={6}>
        <TextField
          light
          fullWidth
          id="lastName"
          label="Last Name"
          placeholder="Last Name"
          value={form.lastName}
          onChange={e => update({ lastName: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={9} md={6}>
        <DatePicker
          light
          fullWidth
          id="birth"
          label="Date of Birth"
          placeholder="mm / dd / yyyy"
          value={form.birth}
          onChange={value => update({ birth: value })}
        />
      </Grid>
      {form.countryFlag === 'CAN' && (
        <Grid item xs={12}>
          <Typography>Identification Number</Typography>
          <Radio.Group
            id="idType"
            name="idType"
            value={form.idType}
            onChange={updateIdType}
          >
            <Grid container spacing={2}>
              <Grid item>
                <Radio value="ssn" id="idType-SSN">
                  American SSN
                </Radio>
              </Grid>
              <Grid item>
                <Radio value="sin" id="idType-SIN">
                  Canadian SIN
                </Radio>
              </Grid>
            </Grid>
          </Radio.Group>
        </Grid>
      )}
      <Grid item xs={12} sm={9} md={6}>
        {form.idType === 'sin' ? (
          <TextFieldSin
            light
            fullWidth
            toggle
            id="sin"
            label="Social Insurance Number"
            placeholder="___-___-___"
            value={form.ssn}
            onChange={updateIdNumber}
          />
        ) : (
          <TextFieldSsn
            light
            fullWidth
            toggle
            id="ssn"
            label="Social Security Number"
            placeholder="___-__-_____ "
            value={form.ssn}
            onChange={updateIdNumber}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={9} md={6}>
        <MaskedInput
          mask={emailMask}
          value={form.email}
          onChange={e => update({ email: e.target.value })}
          render={(ref, props) => (
            <TextField
              inputRef={ref}
              {...props}
              light
              fullWidth
              id="email"
              label="Email"
              placeholder="Email"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={9} md={6}>
        <MaskedInput
          mask={emailMask}
          value={form.confirm}
          onChange={e => update({ confirm: e.target.value })}
          render={(ref, props) => (
            <TextField
              inputRef={ref}
              {...props}
              light
              fullWidth
              id="confirm"
              label="Confirm Email"
              placeholder="Confirm Email"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={9} md={6}>
        <Grid container spacing={2}>
          <Grid item xs={12} className={classes.addressesStatus}>
            {addressIcon()}
            <Typography component="span">
              {state.loading ? (
                'Getting addresses...'
              ) : state.error ? (
                <span className={classes.danger}>
                  {state.error.includes('active:')
                    ? 'User already registered in system (Active).'
                    : state.error.includes('provisioned:')
                    ? 'User already registered in system (Provisioned).'
                    : `An error ocurred trying to get addresses from payroll
                        system.`}
                </span>
              ) : state.addresses.length > 0 ? (
                'Addresses found.'
              ) : (
                `Please enter the user's ${form.idType.toUpperCase()}
                        to retrieve addresses.`
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {state.addresses.length > 0 && (
              <Radio.Group
                id="AddressesGroup"
                name="addresses"
                value={form.address}
                onChange={e => update({ address: e.target.value })}
              >
                <Grid container spacing={0}>
                  {state.addresses.map((address, index) => (
                    <Grid item xs={6} key={index}>
                      <Radio id={`Address${index}`} value={address}>
                        {address}
                      </Radio>
                    </Grid>
                  ))}
                </Grid>
              </Radio.Group>
            )}
          </Grid>
        </Grid>
      </Grid>
      {state.errorSave && (
        <Grid item xs={12} sm={9} md={6}>
          <Grid container spacing={0}>
            <Grid item>
              <Typography
                variant="caption"
                component="div"
                className={classes.danger}
              >
                {state.errorSave}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} sm={9} md={6} className={classes.control}>
        <Button id="SubmitButton" loading={state.loadingSave} onClick={submit}>
          Submit
        </Button>
      </Grid>
    </Grid>
  );
};

export default withPermissionProtection('employee_registrations:create')(
  UserRegistration,
);
